import { useEffect, useState } from 'react';
import { useBrand } from '../brand';
import { ChanceRing, Mark, ResultChip, ScratchedTag } from '../primitives';
import { money } from '../lib/format';
import { betClass, chanceOf, clean, oddsMove, positionOf, type ResultRow, type Selection } from './lib';
import { RunnerDetail } from './RunnerDetail';
import { STANDOUT_LABEL, STANDOUT_TITLE } from './standout';
import { useTrack, useTrackExpand } from '../track';

const SWATCHES: [string, string, string][] = [
  ['win', 'Win', 'Back it to win the race'],
  ['place', 'Place', 'Back it to finish in the first three'],
  ['ew', 'Each-way', 'Half on win, half on place'],
  ['nobet', 'No bet', 'Rated, but not worth a bet at the price'],
  ['scr', 'Scratched', 'Withdrawn from the race'],
];

/** The ⓘ popover: what the card's colours, ring and stats mean. */
function Explainer({ onClose }: { onClose: () => void }) {
  const { markName } = useBrand();
  const [tab, setTab] = useState('colours');
  const track = useTrack();
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);
  const tabs: [string, string][] = [
    ['colours', 'Colours'],
    ['chance', 'Chance'],
    ['stats', 'Stats'],
    ['mark', markName],
  ];
  return (
    <div className="ui-explain" role="dialog" aria-label="What this card shows">
      <div className="tabs" role="tablist">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => {
              setTab(id);
              track('ui_toggle', { control: 'card_explainer_tab', value: id });
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="pane" role="tabpanel">
        {tab === 'colours' && (
          <>
            <div>The card colour shows how we suggest betting on the horse:</div>
            {SWATCHES.map(([k, name, what]) => (
              <div key={k} className={`sw ${k}`}>
                <i />
                <div>
                  <b>{name}</b>
                  <span>{what}</span>
                </div>
              </div>
            ))}
          </>
        )}
        {tab === 'chance' && (
          <div>
            The ring is our chance of this horse winning the race. It combines <b>our price</b> with the market odds and how confident
            we are in the pick, weighed against the other runners we have picked as the likely winners. A full ring is a strong
            chance; a thin sliver is an outsider.
          </div>
        )}
        {tab === 'stats' && (
          <div>
            <b>Last 10</b>: finishing positions of the last ten starts, most recent on the right (x marks a spell). <b>Odds</b>: the
            current market price, with ▼ in or ▲ out showing how it has moved since opening. <b>Our price</b>: what we think the horse
            should be paying; a market price above ours is value.
          </div>
        )}
        {tab === 'mark' && (
          <div>
            <Mark /> marks our strongest selections. Over the last 90 days these have won more often than our other top picks.
            <br />
            <br />
            The dots beside Pick 1 are our confidence indicator: the more that are filled, the more confident we are.
          </div>
        )}
      </div>
      <button type="button" className="got" onClick={onClose}>
        Got it
      </button>
    </div>
  );
}

/** Pick 1's agreement pips: one filled per analysis that made it the top pick, hollow for the rest. */
export function Agreement({ agree, of }: { agree: number; of: number }) {
  const text = `Confidence ${agree} of ${of}`;
  return (
    <span className="ui-agree" role="img" aria-label={text} title={text}>
      {Array.from({ length: of }, (_, i) => (
        <i key={i} className={i < agree ? 'on' : undefined} />
      ))}
    </span>
  );
}

/**
 * One tip. Bet type is the card colour (a solid header or a coloured edge,
 * from [data-card]); the ring is our chance; + opens the full form.
 */
export function RunnerCard({
  sel,
  kind,
  scratched,
  pending,
  result,
  onToggle,
}: {
  sel: Selection;
  kind: 'top' | 'value';
  scratched: boolean;
  /** The race has not run yet. */
  pending: boolean;
  result?: ResultRow | null;
  onToggle?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const expand = useTrackExpand();
  const [info, setInfo] = useState(false);
  const bt = scratched ? 'scr' : betClass(sel.bet_type, sel.bet_stake);
  const horse = clean(sel.horse_name);
  const chance = scratched ? null : chanceOf(sel);
  const move = oddsMove(sel.odds, sel.oddsOpening, sel.oddsCurrent);
  const label = kind === 'value' ? 'Value' : !scratched && sel.pick_order ? `Pick ${sel.pick_order}` : 'Pick';
  const standout = !!sel.isStandardClaim && !scratched;
  const d = sel.detailedInfo ?? {};
  const toggle = () => {
    setOpen(!open);
    onToggle?.(!open);
  };

  return (
    <div className="ui-rcw">
      <article
        className={`ui-rc ${bt}`}
        data-open={open}
        data-standout={standout || undefined}
        aria-label={`${label}: ${horse}${standout ? STANDOUT_LABEL : ''}`}
        title={standout ? STANDOUT_TITLE : undefined}
      >
        <div className="top">
          <span className="num">{sel.number}</span>
          <div className="who">
            <span className="pol">
              <span className="po">{label}</span>
              {scratched ? <ScratchedTag /> : pending ? null : <ResultChip pos={positionOf(result)} sp={result?.price_sp} place={result?.price_place} />}
            </span>
            <span className="hn">
              {horse}
              {kind === 'top' && sel.pick_order === 1 && !scratched && sel.topPickAgreement && (sel.topPickSlots ?? 0) > 1 && (
                <Agreement agree={sel.topPickAgreement} of={sel.topPickSlots!} />
              )}
            </span>
          </div>
          <div className="rt">
            {!scratched && (
              <span className="btns">
                <button
                  type="button"
                  className="ib"
                  aria-label="What this card shows"
                  title="What this card shows"
                  aria-expanded={info}
                  onClick={() => {
                    setInfo(!info);
                    expand('card_explainer', !info, { horseName: horse, selectionType: kind });
                  }}
                >
                  i
                </button>
                <button type="button" className="add" aria-label={`Show full form for ${horse}`} title="Full form" aria-expanded={open} onClick={toggle}>
                  {open ? '−' : '+'}
                </button>
              </span>
            )}
          </div>
          <div className="tags">
            <Mark show={!!sel.isConsensusPick && !scratched} />
          </div>
        </div>
        {/* a scratched runner is just its header strip: no form, prices or connections */}
        {!scratched && (
          <>
            <div className="mid">
              <div>
                <div className="st2">
                  Last 10<b>{d.horse_last10 || '–'}</b>
                </div>
                <div className="st2">
                  Odds
                  <b>
                    {money(move.odds)} {move.label && <span className={`drift ${move.dir}`}>{move.label}</span>}
                  </b>
                </div>
              </div>
              <ChanceRing pct={chance} />
              <div className="rgt">
                <div className="st2">
                  Our price<b>{money(sel.assessedOdds)}</b>
                </div>
              </div>
            </div>
            <div className="ft">
              <span>
                <span className="k">Jockey</span>
                <b>{clean(sel.jockey) || '–'}</b>
              </span>
              <span className="bw">
                <span>
                  Barrier: <b>{sel.barrier || '–'}</b>
                </span>
                <span>
                  Weight: <b>{sel.weight ? `${sel.weight}kg` : '–'}</b>
                </span>
              </span>
              <span className="rgt">
                <span className="k">Trainer</span>
                <b>{clean(sel.trainer) || '–'}</b>
              </span>
            </div>
          </>
        )}
        {!scratched && open && (
          <div className="det">
            <RunnerDetail form={d} stake={sel.bet_stake} analysis={sel.analysis} />
          </div>
        )}
      </article>
      {info && <Explainer onClose={() => setInfo(false)} />}
    </div>
  );
}
